// External Libraries
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { filter, first } from 'lodash'

// Components
import Table from 'pages/Administration/components/Table'
import { frameworkComponents } from 'pages/Administration/constants'

// Config and actions
import { deleteOutputPermission } from './utilities/actions'
import { openModal } from 'components/Modal/actions'
import { detailCellRendererParams, outputColumns } from './utilities/config'

// Helpers and hooks
import {
  excelStyles,
  sizeTableToFit,
  getSimpleNodeId,
  defaultExportParamsOutput,
  sendNotification
} from 'pages/Administration/helpers'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import {
  buildTableActions,
  getRowData,
  outputRoutesSelector
} from './utilities/helpers'
import { DELETE_ROW } from '../../Scenario/constant'
import { useAgGridTranslation } from 'utils/hooks/useDocumentTranslation'

const OutputPanel = ({ roles, output }) => {
  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)

  const { t } = useTranslation(['Administration', 'Common', 'OutputView'])
  const outputRoutes = useStoreSelector('appConfig', outputRoutesSelector)

  const rowData = useMemo(() => getRowData(output), [output])

  const onGridReady = useCallback(({ api, columnApi }) => {
    setGridApi(api)
    setColumnApi(columnApi)
    sizeTableToFit(api)
  }, [])
  const resetSelection = () => {
    setSelectedRow({})
    gridApi.deselectAll()
  }
  const onAdd = useCallback(() => {
    openModal('administrationOutput', {
      modalType: 'CREATE_ROW',
      roles: roles
    })
  }, [roles])

  const onDelete = useCallback(() => {
    openModal('confirmAction', {
      message: t('OutputManagement.DeleteOutputPermissions'),
      onConfirm: () => {
        deleteOutputPermission(selectedRow)
          .then(() => {
            sendNotification({ t, info: DELETE_ROW, kind: 'success' })
            resetSelection()
          })
          .catch(() => {
            sendNotification({
              t,
              info: DELETE_ROW,
              kind: 'error'
            })
          })
      },
      loadingOverlay: true
    })
  }, [selectedRow])

  const findLabel = (outputPage) => {
    const findOutput = filter(outputRoutes, {
      component: outputPage
    })
    const output = first(findOutput)
    return t(`OutputView:${output?.title}`) + ' - ' + output?.path ?? outputPage
  }

  const onCellClicked = useCallback(
    ({ data: { roleName, permissions } }) => {
      openModal('administrationOutput', {
        modalType: 'UPDATE_ROW',
        roles: roles,
        outputValue: { roleName, permissions },
        gridApi: gridApi
      })
    },
    [roles]
  )

  const headerActions = useMemo(() => {
    if (gridApi && columnApi) {
      return buildTableActions({
        t,
        gridApi,
        columnApi,
        onAdd,
        onDelete
      })
    }
  }, [t, gridApi, columnApi, onAdd, onDelete])

  const exportParamsOutput = useMemo(() => defaultExportParamsOutput(t), [t])

  const { t: agGridT, destroyed } = useAgGridTranslation()

  return (
    <div className='role-view'>
      <div className='children-view'>
        <Table
          defaultExportParams={exportParamsOutput}
          suppressContextMenu
          masterDetail
          rowData={rowData}
          onGridReady={onGridReady}
          excelStyles={excelStyles}
          columnDefs={outputColumns(t)}
          localeTextFunc={agGridT}
          headerActions={headerActions}
          getRowNodeId={getSimpleNodeId}
          frameworkComponents={frameworkComponents}
          detailCellRendererParams={detailCellRendererParams(t, findLabel)}
          onCellClicked={onCellClicked}
          onSelectionChanged={(rows) => {
            setSelectedRow(first(rows) ?? {})
          }}
        />
      </div>
    </div>
  )
}

export default React.memo(OutputPanel)
